import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { MODULE_SEQUENCE, COURSES } from '../data/curriculum';
import { supabase, isSupabaseConfigured } from '../lib/supabase';

interface UserProfile {
    id: string;
    username: string;
    avatar: string;
    createdAt?: string;
}

interface ProgressState {
    completedModules: string[];
    unlockedCourses: string[];
    xp: number;
    badges: string[];
    completedCourses: string[]; // Official completion (passed quiz)
    certificates: Record<string, { id: string; date: string }>;
}

export interface Certificate {
    id: string;
    courseId: string;
    date: string;
    studentName: string;
}

interface AcademyStore {
    // User State
    currentUser: UserProfile | null;
    allUsers: UserProfile[];
    isLoading: boolean;
    isOnline: boolean;

    // Progress State
    progress: Record<string, ProgressState>;

    // Actions
    createUser: (username: string, avatar: string) => void;
    loginUser: (user: UserProfile | string) => void;
    logoutUser: () => void;

    // Supabase Sync
    syncWithSupabase: () => Promise<void>;
    loadFromSupabase: () => Promise<void>;

    // Progress Actions
    // Progress Actions
    completeModule: (moduleId: string, xpReward: number, badgeId?: string) => void;
    completeCourse: (courseId: string) => void;

    // Feature Flags / Settings
    settings: { voiceEnabled: boolean };
    toggleVoice: () => void;

    // Debug State
    debugMode: boolean;
    toggleDebug: () => void;
    unlockAll: () => void;
    resetProgress: () => Promise<void>;
}

// Debounce helper for Supabase sync
let syncTimeout: ReturnType<typeof setTimeout> | null = null;
const debouncedSync = (fn: () => void) => {
    if (syncTimeout) clearTimeout(syncTimeout);
    syncTimeout = setTimeout(fn, 2000);
};

export const useAcademyStore = create<AcademyStore>()(
    persist(
        (set, get) => ({
            currentUser: null,
            allUsers: [],
            progress: {},
            settings: { voiceEnabled: false },
            debugMode: false,
            isLoading: false,
            isOnline: isSupabaseConfigured(),

            toggleVoice: () => set(state => ({
                settings: { ...state.settings, voiceEnabled: !state.settings.voiceEnabled }
            })),

            toggleDebug: () => set(state => ({ debugMode: !state.debugMode })),

            unlockAll: () => {
                const user = get().currentUser;
                if (!user) return;

                const ALL_MODULES = MODULE_SEQUENCE;
                const ALL_COURSES = COURSES.map(c => c.id);

                const ALL_BADGES = [
                    'badge_digital-citizen', 'badge_foundation', 'badge_production',
                    'badge_data-detective', 'badge_ai', 'badge_cyber-security',
                    'badge_automation', 'badge_ui-ux', 'badge_systems', 'badge_game-dev',
                    'badge_variables', 'badge_loops', 'badge_architecture', 'badge_functions',
                    'badge_relational', 'badge_logic-architect'
                ];

                set(state => ({
                    progress: {
                        ...state.progress,
                        [user.id]: {
                            ...state.progress[user.id],
                            completedModules: ALL_MODULES,
                            unlockedCourses: ALL_COURSES,
                            badges: ALL_BADGES,
                            xp: ALL_MODULES.length * 100
                        }
                    }
                }));

                // Sync to Supabase
                debouncedSync(() => get().syncWithSupabase());
            },

            resetProgress: async () => {
                const user = get().currentUser;
                if (!user) return;

                const emptyProgress = {
                    completedModules: [],
                    unlockedCourses: ['digital-foundations'],
                    xp: 0,
                    badges: [],
                    completedCourses: [],
                    certificates: {}
                };

                set(state => ({
                    progress: {
                        ...state.progress,
                        [user.id]: emptyProgress
                    }
                }));

                // Force immediate sync to clear Supabase data
                await get().syncWithSupabase();
                console.log('✅ Progress fully reset for user:', user.username);
            },

            createUser: (username, avatar) => {
                const newUser: UserProfile = {
                    id: crypto.randomUUID(),
                    username,
                    avatar,
                    createdAt: new Date().toISOString(),
                };

                set((state) => ({
                    allUsers: [...state.allUsers, newUser],
                    currentUser: newUser,
                    progress: {
                        ...state.progress,
                        [newUser.id]: {
                            completedModules: [],
                            unlockedCourses: ['digital-foundations'],
                            xp: 0,
                            badges: [],
                            completedCourses: [],
                            certificates: {}
                        }
                    }
                }));
            },

            loginUser: (userOrId) => {
                // Handle both object (from AuthScreen) and string ID (legacy)
                if (typeof userOrId === 'object') {
                    const user = userOrId as UserProfile;

                    set((state) => {
                        // Add to allUsers if not exists
                        const exists = state.allUsers.some(u => u.id === user.id);
                        const allUsers = exists ? state.allUsers : [...state.allUsers, user];

                        // Always start with clean progress for this user
                        // Supabase will overwrite with real data momentarily
                        return {
                            currentUser: user,
                            allUsers,
                            progress: {
                                [user.id]: state.progress[user.id] || {
                                    completedModules: [],
                                    unlockedCourses: ['digital-foundations'],
                                    xp: 0,
                                    badges: [],
                                    completedCourses: [],
                                    certificates: {}
                                }
                            }
                        };
                    });

                    // Load progress from Supabase (overwrites local)
                    get().loadFromSupabase();
                } else {
                    // Legacy: string ID
                    const user = get().allUsers.find(u => u.id === userOrId);
                    if (user) {
                        set({ currentUser: user });
                    }
                }
            },

            logoutUser: async () => {
                // Sync before logout
                await get().syncWithSupabase();

                // Sign out of Supabase
                if (isSupabaseConfigured()) {
                    await supabase.auth.signOut();
                }

                set({ currentUser: null });
            },

            // Sync local progress to Supabase
            syncWithSupabase: async () => {
                const { currentUser, progress } = get();
                if (!currentUser || !isSupabaseConfigured()) return;

                const userProgress = progress[currentUser.id];
                if (!userProgress) return;

                try {
                    // Update user metadata with progress
                    const { error } = await supabase.auth.updateUser({
                        data: {
                            username: currentUser.username,
                            avatar: currentUser.avatar,
                            progress: userProgress
                        }
                    });

                    if (error) {
                        console.error('Failed to sync to Supabase:', error);
                    } else {
                        console.log('✅ Progress synced to Supabase');
                    }
                } catch (err) {
                    console.error('Supabase sync error:', err);
                }
            },

            // Load progress from Supabase
            loadFromSupabase: async () => {
                const { currentUser } = get();
                if (!currentUser || !isSupabaseConfigured()) return;

                set({ isLoading: true });

                try {
                    const { data: { user } } = await supabase.auth.getUser();

                    if (user?.user_metadata?.progress) {
                        const remoteProgress = user.user_metadata.progress;

                        set((state) => ({
                            progress: {
                                ...state.progress,
                                [currentUser.id]: {
                                    completedModules: remoteProgress.completedModules || [],
                                    unlockedCourses: remoteProgress.unlockedCourses || ['digital-foundations'],
                                    xp: remoteProgress.xp || 0,
                                    badges: remoteProgress.badges || [],
                                    completedCourses: remoteProgress.completedCourses || [],
                                    certificates: remoteProgress.certificates || {}
                                }
                            }
                        }));

                        console.log('✅ Progress loaded from Supabase');
                    }
                } catch (err) {
                    console.error('Failed to load from Supabase:', err);
                } finally {
                    set({ isLoading: false });
                }
            },

            completeModule: (moduleId, xpReward, badgeId?) => {
                const user = get().currentUser;
                if (!user) return;

                set((state) => {
                    const userProgress = state.progress[user.id] || {
                        completedModules: [],
                        unlockedCourses: [],
                        xp: 0,
                        badges: [],
                        completedCourses: [],
                        certificates: {}
                    };

                    const isNewModule = !userProgress.completedModules.includes(moduleId);
                    const updatedCompletedModules = isNewModule
                        ? [...userProgress.completedModules, moduleId]
                        : userProgress.completedModules;

                    const newBadges = userProgress.badges ? [...userProgress.badges] : [];
                    if (badgeId && !newBadges.includes(badgeId)) {
                        newBadges.push(badgeId);
                    }

                    // CHECK COURSE COMPLETION (Legacy: All Modules)
                    const courseBadgeMap: Record<string, string> = {
                        'digital-foundations': 'badge_digital-citizen',
                        'foundation': 'badge_foundation',
                        'production': 'badge_production',
                        'data-detective': 'badge_data-detective',
                        'ai-operator': 'badge_ai',
                        'cyber-security': 'badge_cyber-security',
                        'automation': 'badge_automation',
                        'ui-ux': 'badge_ui-ux',
                        'sovereign-systems': 'badge_systems',
                        'game-dev': 'badge_game-dev',
                        'logic-architect': 'badge_logic-architect'
                    };

                    let nextUnlockedCourses = [...(userProgress.unlockedCourses || [])];

                    // Find which course this module belongs to
                    const parentCourse = COURSES.find(c => c.modules.some(m => m.id === moduleId));

                    if (parentCourse) {
                        // Check if ALL modules in this course are now completed
                        const allModulesCompleted = parentCourse.modules.every(m =>
                            updatedCompletedModules.includes(m.id)
                        );

                        if (allModulesCompleted) {
                            const courseBadgeId = courseBadgeMap[parentCourse.id];
                            if (courseBadgeId && !newBadges.includes(courseBadgeId)) {
                                newBadges.push(courseBadgeId);
                            }

                            // Unlock next course
                            const currentCourseIndex = COURSES.findIndex(c => c.id === parentCourse.id);
                            if (currentCourseIndex !== -1 && currentCourseIndex < COURSES.length - 1) {
                                const nextCourse = COURSES[currentCourseIndex + 1];
                                if (!nextUnlockedCourses.includes(nextCourse.id)) {
                                    nextUnlockedCourses.push(nextCourse.id);
                                }
                            }
                        }
                    }

                    console.log(`[AcademyStore] Module Completed: ${moduleId}. Unlocked Courses:`, nextUnlockedCourses);

                    return {
                        progress: {
                            ...state.progress,
                            [user.id]: {
                                ...userProgress,
                                completedModules: updatedCompletedModules,
                                unlockedCourses: nextUnlockedCourses, // Explicitly return the new list
                                xp: userProgress.xp + xpReward,
                                badges: newBadges
                            }
                        }
                    };
                });

                // AUTO-COMPLETE COURSE for Finals
                if (moduleId.endsWith('-final-exam')) {
                    const courseId = moduleId.replace('-final-exam', '');
                    console.log(`[AcademyStore] Final Exam passed for ${courseId}. Auto-completing course.`);
                    get().completeCourse(courseId);
                }

                // Sync to Supabase (debounced)
                debouncedSync(() => get().syncWithSupabase());
            },

            completeCourse: (courseId) => {
                const user = get().currentUser;
                if (!user) return;

                set((state) => {
                    const userProgress = state.progress[user.id];
                    if (!userProgress) return state;

                    // 1. Calculate Unlocked Courses (Force unlock next course)
                    const currentCourseIndex = COURSES.findIndex(c => c.id === courseId);
                    let nextUnlockedCourses = [...(userProgress.unlockedCourses || [])];

                    if (currentCourseIndex !== -1 && currentCourseIndex < COURSES.length - 1) {
                        const nextCourse = COURSES[currentCourseIndex + 1];
                        if (!nextUnlockedCourses.includes(nextCourse.id)) {
                            nextUnlockedCourses.push(nextCourse.id);
                        }
                    }

                    // 2. Handle Certificate & Completion Status
                    const isAlreadyCompleted = userProgress.completedCourses.includes(courseId);

                    if (isAlreadyCompleted) {
                        // Just update unlocks if needed
                        return {
                            progress: {
                                ...state.progress,
                                [user.id]: {
                                    ...userProgress,
                                    unlockedCourses: nextUnlockedCourses
                                }
                            }
                        };
                    }

                    // New Completion
                    const certId = crypto.randomUUID().split('-')[0].toUpperCase();

                    return {
                        progress: {
                            ...state.progress,
                            [user.id]: {
                                ...userProgress,
                                unlockedCourses: nextUnlockedCourses,
                                completedCourses: [...userProgress.completedCourses, courseId],
                                certificates: {
                                    ...userProgress.certificates,
                                    [courseId]: {
                                        id: `GGA-${new Date().getFullYear()}-${certId}`,
                                        date: new Date().toLocaleDateString()
                                    }
                                }
                            }
                        }
                    };
                });

                debouncedSync(() => get().syncWithSupabase());
            }
        }),
        {
            name: 'grounded-growth-storage',
            storage: createJSONStorage(() => localStorage),
        }
    )
);

// Initialize: Check for existing Supabase session on app load
if (typeof window !== 'undefined') {
    supabase.auth.onAuthStateChange(async (event, session) => {
        if (event === 'SIGNED_IN' && session?.user) {
            const metadata = session.user.user_metadata;
            useAcademyStore.getState().loginUser({
                id: session.user.id,
                username: metadata?.username || session.user.email?.split('@')[0] || 'Scout',
                avatar: metadata?.avatar || 'gorilla_working',
            });
        } else if (event === 'SIGNED_OUT') {
            useAcademyStore.setState({ currentUser: null });
        }
    });
}
